import { toast } from "react-toastify";

import { STATUS_CODE } from "@/constants/errorCode";
import { queryClient } from "@/lib/react-query";
import { resetStore, store } from "@/store";
import { setAuth } from "@/store/slice/authSlice";

import { datadogRum } from "@datadog/browser-rum";
import type { AxiosError } from "axios";
import axios from "axios";

// Flag to mark if a token refresh is in progress
let isRefreshing = false;

// Array to hold callbacks for when the token is refreshed
let refreshSubscribers: ((token: string) => void)[] = [];

// Subscribe to token refresh
function subscribeTokenRefresh(callback: (token: string) => void) {
  refreshSubscribers.push(callback);
}

// Execute all subscribers
function onRefreshed(token: string) {
  refreshSubscribers.forEach((callback) => callback(token));
  refreshSubscribers = [];
}

export const PATH_WITHOUT_AUTH_HEADER = [
  /\/auth\/token$/,
  /\/auth\/verify-token$/,
  /\/auth\/forgot-password$/,
  /\/auth\/reset-password$/,
  /\/auth\/sso\/providers$/,
  /\/activate-user$/,
  /\/partners\/.+$/ // This matches /partners/ followed by anything
];

const LOGOUT_PATH = /\/auth\/logout$/;

export const baseUrl = import.meta.env.VITE_API_URL || "";

const axiosInstance = axios.create({
  baseURL: baseUrl,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json"
  },
  withCredentials: true
});

// This function will only be called when any api / api logout has been retried
const doLogout = async () => {
  // No matter what happens to the logout API call
  // Reset the store to initial state + purge persisted data
  resetStore();

  toast.dismiss();

  // Clear all react-query cache
  queryClient.clear();

  // Clear datadog user
  datadogRum.clearUser();

  // Call logout API then clear auth state on server side
  await axiosInstance.post("/v1/auth/logout");
};

const isUnauthorized = (status: number) => status === STATUS_CODE.UNAUTHORIZED;

const doRefreshToken = async (
  originalConfig: AxiosError["config"] & { _retry?: boolean }
) => {
  // Mark this request as retried
  originalConfig._retry = true;

  // If we're not currently refreshing
  if (!isRefreshing) {
    isRefreshing = true;
    try {
      // Only one request will actually call the refresh API
      const result = await axiosInstance.post("/v1/auth/token", {
        grant_type: "refresh_token",
        refresh_token: store.getState().auth.refreshToken
      });
      const access_token = result?.data?.access_token;
      if (access_token) {
        // Update token in store
        store.dispatch(setAuth({ accessToken: access_token }));
      }

      // Notify all waiting requests that token is refreshed
      onRefreshed(access_token);

      // Reset refreshing state
      isRefreshing = false;

      // Retry original request with new token
      originalConfig.headers["Authorization"] = `Bearer ${access_token}`;
      return axiosInstance(originalConfig);
    } catch (err) {
      // Refresh failed - logout
      isRefreshing = false;
      await doLogout();
      return Promise.reject(err as Error);
    }
  } else {
    // For all other requests that came in while refreshing
    // Create a new promise that will resolve when token is refreshed
    return new Promise((resolve) => {
      subscribeTokenRefresh((access_token: string) => {
        // Replace old token with new one
        originalConfig.headers["Authorization"] = `Bearer ${access_token}`;
        // Retry the request
        resolve(axiosInstance(originalConfig));
      });
    });
  }
};

axiosInstance.interceptors.request.use(
  (config) => {
    const token = store.getState().auth.accessToken;

    // Check if the current URL path should skip auth header
    const shouldSkipAuthHeader = PATH_WITHOUT_AUTH_HEADER.some((pathPattern) =>
      pathPattern.test(config.url ?? "")
    );

    // Only add Authorization header if token exists and URL is not in the excluded list
    if (token && !shouldSkipAuthHeader) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error as Error);
  }
);

axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalConfig = error.config;
    const currentUrl = originalConfig.url ?? "";
    const shouldSkipRefreshToken =
      LOGOUT_PATH.test(currentUrl) || // if on logout path, skip the refresh token
      PATH_WITHOUT_AUTH_HEADER.some((pathPattern) =>
        pathPattern.test(currentUrl)
      );
    originalConfig._retry = originalConfig._retry ?? false;
    // Check for 401 status and ensure we haven't retried this request already
    if (!shouldSkipRefreshToken && isUnauthorized(error?.response?.status)) {
      if (originalConfig._retry) {
        // Logout user if after success refresh token, the API is still unauthorized
        await doLogout();
      } else {
        return doRefreshToken(originalConfig);
      }
    }
    return Promise.reject(error as Error);
  }
);

export default axiosInstance;
